import React, { useMemo } from 'react';
import CustomInput from 'components/CustomInput/CustomInput';
import { translate } from 'components/Translation';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import CustomRadio from 'components/CustomRadio/CustomRadio';
import CanPerform from 'components/CanPerform/CanPerform';
import { BodyS } from 'components/Typography/Typography';
import styles from 'assets/jss/material-dashboard-pro-react/views/users/addUserDialogStyle';
import { makeStyles } from '@material-ui/core';
import emailNotValid from 'utils/objectsValidation/emailNotValid';
import { PHONE_NUMBER_LENGTH } from 'utils/const/numbers';
import { SingleCorpAccount } from 'utils/api/models/accounts/singleCorpAccount';
import { MAX_ADMIN_COUNT } from 'utils/const/users';

// @ts-ignore
const useStyles = makeStyles(styles);

interface IUserTypeTabProps {
	showValidation: boolean;
	userType?: types.users.userType;
	selectedUserType: types.users.userType;
	setSelectedUserType: (userType: types.users.userType) => void;
	emailAddress: string;
	setEmailAddress: (email: string) => void;
	userName: string;
	setUserName: (userName: string) => void;
	userExistsError: boolean;
	setUserExistsError: (error: boolean) => void;
	contactNumber: string;
	setContactNumber: (contactNumber: string) => void;
	accounts: SingleCorpAccount[];
}

const UserTypeTab = ({
	showValidation,
	selectedUserType,
	setSelectedUserType,
	userType,
	emailAddress,
	setEmailAddress,
	setUserName,
	userName,
	userExistsError,
	setUserExistsError,
	contactNumber,
	setContactNumber,
	accounts,
}: IUserTypeTabProps) => {
	const classes = useStyles();

	const errorMessage: string = useMemo(() => {
		if (showValidation && emailNotValid(emailAddress)) {
			return translate('error.invalid.email.format');
		} else if (userExistsError) {
			return translate('error.email-exists');
		} else {
			return '';
		}
	}, [showValidation, userExistsError, emailAddress]);

	const onContactNumberChange = (event: React.FormEvent<HTMLInputElement>) => {
		const value = (event.target as HTMLInputElement).value;
		if (value.length <= PHONE_NUMBER_LENGTH) {
			setContactNumber(value);
		}
	};

	const preparedAccounts = accounts?.map((account) => ({
		...account,
		disabled: account?.adminsCount >= MAX_ADMIN_COUNT,
	}));

	const allAccountsDisabled = preparedAccounts?.every(({ disabled }) => disabled);

	return (
		<div>
			<ConditionalRender show={allAccountsDisabled}>
				<div>
					<BodyS>{translate('users.details.maxTwoAdmin')}</BodyS>
				</div>
			</ConditionalRender>
			<CustomInput
				labelText={translate('user.name')}
				formControlProps={{
					fullWidth: true,
				}}
				id="username-input"
				inputProps={{
					value: userName,
					onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
						setUserName(event.target.value);
					},
				}}
			/>
			<CustomInput
				labelText={
					selectedUserType === 'ENDUSER' ? translate('add.user.email.address') : translate('email.address')
				}
				formControlProps={{
					fullWidth: true,
				}}
				id="email-input"
				inputProps={{
					value: emailAddress,
					onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
						setUserExistsError(false);
						setEmailAddress(event.target.value);
					},
				}}
				error={(showValidation && emailNotValid(emailAddress)) || userExistsError}
				helperText={
					<ConditionalRender show={(showValidation && emailNotValid(emailAddress)) || userExistsError}>
						<span className={classes.error}>{errorMessage}</span>
					</ConditionalRender>
				}
			/>
			<ConditionalRender
				show={(userType && userType !== 'ENDUSER') || selectedUserType !== 'ENDUSER'}
				onTrue={() => (
					<div className={classes.contractInputWrapper}>
						<CustomInput
							labelText={translate('contact.number')}
							formControlProps={{
								fullWidth: true,
							}}
							id="contactNumber-input"
							inputProps={{
								value: contactNumber,
								onChange: onContactNumberChange,
								type: 'number',
							}}
							error={showValidation && contactNumber.length !== PHONE_NUMBER_LENGTH}
							helperText={
								<ConditionalRender
									show={showValidation && contactNumber.length !== PHONE_NUMBER_LENGTH}
								>
									<span className={classes.error}>
										{translate('error.invalid.mobileNumber.format')}
									</span>
								</ConditionalRender>
							}
						/>
					</div>
				)}
			/>
			{!userType && (
				<>
					<div className={classes.userTypeBody}>
						<BodyS disableMargin>{translate('select.user.type')}</BodyS>
					</div>
					<div className={classes.userTypeWrapper}>
						<CustomRadio
							label={translate('user.type.enduser')}
							checked={selectedUserType === 'ENDUSER'}
							onChange={() => setSelectedUserType('ENDUSER')}
						/>
						<CustomRadio
							label={translate('user.type.manager')}
							checked={selectedUserType === 'MANAGER'}
							onChange={() => setSelectedUserType('MANAGER')}
						/>
						<CanPerform
							action="users:add-admin"
							yes={
								<CustomRadio
									label={translate('user.type.admin')}
									checked={selectedUserType === 'ADMIN'}
									onChange={() => setSelectedUserType('ADMIN')}
									disabled={allAccountsDisabled}
								/>
							}
						/>
					</div>
				</>
			)}
		</div>
	);
};

export default UserTypeTab;
